import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { AuthService, UtmMarketingFields } from "@front/m19-services";
import { UntilDestroy } from "@ngneat/until-destroy";

import { toSignal } from "@angular/core/rxjs-interop";
import { Currency, UserBase } from "@front/m19-api-client";
import { IAlertComponent, MarketingField, RegisterFormComponent, RegisterFormValue } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { Angulartics2 } from "angulartics2";
import { map } from "rxjs";

@UntilDestroy()
@Component({
  selector: "app-register",
  templateUrl: "./register.component.html",
  imports: [IAlertComponent, RegisterFormComponent, TranslocoDirective, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private angulartics2 = inject(Angulartics2);
  private layoutSelector = inject(LayoutSelectorService);
  private route = inject(ActivatedRoute);
  private readonly utmMarketingFields = toSignal(
    this.route.data.pipe(
      map((data) => {
        return (data as { marketingFields: UtmMarketingFields }).marketingFields;
      }),
    ),
  );

  termsAndConditionLink = this.layoutSelector.getTermsAndConditionLink();
  agency = this.layoutSelector.getAgencyName();

  loading = signal(false);
  error = signal<string | null>(null);

  marketingFields =
    this.layoutSelector.getBoardType() === BoardType.M19
      ? [MarketingField.HowDidYouHearAboutUs, MarketingField.MonthlyAdSpend]
      : undefined;

  register(formValue: RegisterFormValue): void {
    if (!formValue) return;
    this.loading.set(true);
    const user = this.toUserBase(formValue);

    this.authService.register(user).subscribe({
      next: () => {
        this.angulartics2.eventTrack.next({
          action: "register",
          properties: { category: "register", label: "success_" + user.email },
        });
        this.goToHomeOrRedirect();
      },
      error: (err) => {
        this.error.set("User registration error: " + (err.response?.message ?? err.message));
        this.loading.set(false);
      },
    });
  }

  private toUserBase(formValue: RegisterFormValue): UserBase {
    const fullName: string = formValue.firstName.trim() + " " + formValue.lastName.trim();
    return {
      defaultCurrency: Currency.USD, // TODO select according to user accounts and browser language
      userName: fullName,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      companyName: formValue.companyName,
      phoneNumber: !formValue.phoneNumber ? undefined : formValue.phoneNumber,
      monthlyAdSpend: formValue.monthlyAdSpend!,
      howDidYouHearAboutUs: formValue.howDidYouHearAboutUs,
      email: formValue.email,
      newPassword: formValue.password,
      registered: true, // Already checked by the form
      language: formValue.language,
      utmCampaign: this.utmMarketingFields()?.utmCampaign,
      utmSource: this.utmMarketingFields()?.utmSource,
      utmMedium: this.utmMarketingFields()?.utmMedium,
    };
  }

  goToHomeOrRedirect(): void {
    const redirectUrl = this.route.snapshot.queryParams["redirect"] ?? "dashboard";
    this.router.navigateByUrl(redirectUrl);
  }
}
